import React, { Component, createRef } from "react";
import { FirebaseService, UserNotificationService } from "@services";
import utils from "@utils";
import { Switch, ConfirmationModal } from "../../../components";

const notificationsSupported = FirebaseService.messaging !== null;

class Notifications extends Component {
	constructor(props) {
		super(props);

		this.state = {
			notificationsEnabled: false,
			notificationSettings: [],
		};
		this.smsOptInModal = createRef();
	}

	componentDidMount() {
		const { lang } = global;

		FirebaseService.getNotificationsEnabled().then(notificationsEnabled => {
			this.setState({ notificationsEnabled });
		});

		UserNotificationService.getNotificationSettings()
			.then(({ data }) => {
				// Sort notifications to make sure all of them are ordered by: 1. Email, 2. SMS, 3. Push
				const config = data.map(item => ({
					...item,
					NotificationMethods: item.NotificationMethods.sort(
						(a, b) => a.MethodType - b.MethodType,
					),
				}));

				this.setState({ notificationSettings: config });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading notification settings"),
				});

				this.setState({ notificationSettings: [] });
			});
	}

	/** Toggles the users browser based push notification settings */
	toggleBrowserNotifications = async () => {
		const { notificationsEnabled } = this.state;

		if (notificationsEnabled) {
			FirebaseService.removeToken();
			this.setState({ notificationsEnabled: false });
		} else {
			const granted = await FirebaseService.requestPermission();
			this.setState({ notificationsEnabled: granted });
		}
	};

	/**
	 * Toggles the users notification settings (Email, SMS, Push)
	 * @param {Number} groupType The settings group being toggled (Invoice Status or Officer Status Updates)
	 * @param {Number} methodType The type of notification being toggled (Email, SMS, Push)
	 * @returns {Array} The group of notification settings
	 */
	handleToggleChange = (groupType, method) => {
		const { lang } = global;
		const { notificationSettings } = this.state;
		const optedIntoSMS = notificationSettings.some(({ NotificationMethods }) =>
			NotificationMethods.some(m => m.Enabled && m.MethodType === 2),
		);
		// if toggling SMS notification (MethodType === 2) & the user does not have other SMS notifications enabled, show opt-in confirmation modal
		if (method.MethodType === 2 && !method.Enabled && !optedIntoSMS) {
			this.smsOptInModal.openModal({
				title: "Enable SMS",
				// eslint-disable-next-line max-len
				message: lang.t(
					"By enabling this toggle, you consent to receive SMS messages (including text messages) from RollKall, notwithstanding any current or prior election to opt in or opt out of receiving SMS messages, at the specific number(s) you have provided to us, or numbers we can reasonably associate with your account, with information or questions related to your account_",
				),
				confirmButton: lang.t("I agree"),
				onConfirm: () => this.toggleNotificationSettings(groupType, method.MethodType),
			});
		} else {
			this.toggleNotificationSettings(groupType, method.MethodType);
		}
	};

	/**
	 * Toggles the users notification settings (Email, SMS, Push)
	 * @param {Number} groupType The settings group being toggled (Invoice Status or Officer Status Updates)
	 * @param {Number} methodType The type of notification being toggled (Email, SMS, Push)
	 * @returns {Array} The group of notification settings
	 */
	toggleNotificationSettings = (groupType, methodType) => {
		const { lang } = global;
		const { notificationSettings } = this.state;

		// Keep current settings in case the update fails and we have to rollback
		const currentSettings = { ...notificationSettings };

		// Find and set the new notification value
		const newSettings = notificationSettings.map(group => {
			if (group.GroupType === groupType) {
				return {
					...group,
					NotificationMethods: group.NotificationMethods.map(item =>
						item.MethodType === methodType ? { ...item, Enabled: !item.Enabled } : item,
					),
				};
			}

			return group;
		});

		// Set the state before calling the API to update the UI
		this.setState({ notificationSettings: newSettings });

		UserNotificationService.updateNotificationSettings(newSettings)
			.then(({ data }) => {
				this.setState({ notificationSettings: data });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error updating notification settings"),
				});

				// Restore original settings in case the endpoint failed
				this.setState({ notificationSettings: currentSettings });
			});
	};

	render() {
		const { lang } = global;
		const { notificationSettings, notificationsEnabled } = this.state;

		return (
			<div className="my-account-panel">
				<div className="my-account-panel-header notifications-header">
					<h4 className="title">{lang.t("Notifications")}</h4>
					<If condition={notificationsSupported}>
						<div id="my-account-toggle-notifications">
							<span>{lang.t("Enable push notifications for this device")}</span>
							<Switch
								onClick={this.toggleBrowserNotifications}
								on={notificationsEnabled}
							/>
						</div>
					</If>
				</div>
				<div className="my-account-panel-body notifications-body">
					<div className="row">
						<div className="col-xs-4" />
						<div className="col-xs-8">
							<div className="row">
								<div className="col-xs-4 text-center">{lang.t("EMail")}</div>
								<div className="col-xs-4 text-center">{lang.t("SMS")}</div>
								<div className="col-xs-4 text-center">{lang.t("Push")}</div>
							</div>
						</div>
					</div>
					<For
						each="group"
						of={notificationSettings}
					>
						<div
							key={`notification-type-${group.GroupType}`}
							className="row"
						>
							<div className="col-xs-4 text-right my-account-notification-group">
								{group.GroupName}
							</div>
							<div className="col-xs-8">
								<div className="row">
									<For
										each="method"
										of={group.NotificationMethods}
									>
										<div
											className="col-xs-4 text-center"
											key={`notification-setting-${method.GroupType}-${method.MethodType}`}
										>
											<Switch
												onClick={() =>
													this.handleToggleChange(group.GroupType, method)
												}
												on={method.Enabled ?? false}
												disabled={!method.Customizable ?? true}
											/>
										</div>
									</For>
								</div>
							</div>
						</div>
					</For>
				</div>
				<ConfirmationModal
					ref={ref => {
						this.smsOptInModal = ref;
					}}
				/>
			</div>
		);
	}
}

export default Notifications;
